import type { BottleneckSegmentKey } from '../../../../redux/api/bottleneck-report';

const HOUR_SECONDS = 3600;
const DAY_SECONDS = 86400;

/**
 * Colours read as a pipeline rather than a rainbow: grey for work nobody has
 * touched, blue while engineering has it, amber for every stage where the shop
 * is waiting on somebody else, green once it can move, and red for
 * Uncategorized -- a job in red is a data problem, not a slow stage.
 */
export const BUCKET_COLORS: Record<BottleneckSegmentKey, string> = {
	new: '#bfbfbf',
	material_selected: '#91caff',
	program_review: '#4096ff',
	waiting_on_material: '#fa8c16',
	waiting_on_kitting: '#faad14',
	ready_to_kit_and_cut: '#36cfc9',
	waiting_on_cutting: '#ffd666',
	ready_to_machine: '#95de64',
	machining: '#389e0d',
	done: '#722ed1',
	uncategorized: '#ff4d4f',
	delivered: '#8c8c8c',
	remaining: '#bae0ff',
};

/** Past due reads as its own state, not as more of the same runway. */
export const OVERDUE_COLOR = '#ff7875';

export const SEGMENT_LABELS: Record<BottleneckSegmentKey, string> = {
	new: 'New',
	material_selected: 'Material Selected',
	program_review: 'Program Review',
	waiting_on_material: 'Waiting on Material',
	waiting_on_kitting: 'Waiting on Kitting',
	ready_to_kit_and_cut: 'Ready to Kit and Cut',
	waiting_on_cutting: 'Waiting on Cutting',
	ready_to_machine: 'Ready to Machine',
	machining: 'Machining',
	done: 'Done',
	uncategorized: 'Uncategorized',
	delivered: 'Delivered',
	remaining: 'Time remaining',
};

/**
 * Whole hours under a day, then days to a tenth. Nobody on the floor plans
 * around "3 days 7 hours 12 minutes", and the extra digits make a column of
 * these impossible to scan.
 */
export const formatDuration = (seconds: number | null | undefined) => {
	if (seconds === null || seconds === undefined) return '—';
	if (seconds < DAY_SECONDS) return `${Math.round(seconds / HOUR_SECONDS)}h`;
	return `${(seconds / DAY_SECONDS).toFixed(1)}d`;
};

export const formatCount = (value: number) => value.toLocaleString();

export const formatMoney = (amount: string | number | null | undefined) => {
	if (amount === null || amount === undefined) return '—';
	const value = Number(amount);
	if (Number.isNaN(value)) return '—';
	return value.toLocaleString(undefined, {
		style: 'currency',
		currency: 'USD',
		maximumFractionDigits: 0,
	});
};

/**
 * The length every bar is measured against.
 *
 * Using the longest job would let one forgotten line item squash the other
 * three hundred flat, so the scale is the 95th percentile and the handful
 * above it are drawn full width and flagged instead.
 */
export const barScale = (durations: number[]) => {
	const usable = durations.filter((value) => value > 0).sort((a, b) => a - b);
	if (!usable.length) return DAY_SECONDS;
	const index = Math.floor((usable.length - 1) * 0.95);
	return Math.max(usable[index], HOUR_SECONDS);
};
